/**
 * Formatting utilities for financial data
 * Handles currency, percentages, multiples, dates, and numeric values
 */

/**
 * Format a number as currency with optional decimal places
 * @param value - The numeric value to format
 * @param decimals - Number of decimal places (default: 2)
 * @param showSymbol - Whether to show the $ symbol (default: true)
 * @returns Formatted currency string (e.g., "$1,234.56")
 *
 * @example
 * formatCurrency(1234.567) // "$1,234.57"
 * formatCurrency(1234.567, 1) // "$1,234.6"
 * formatCurrency(1234.567, 2, false) // "1,234.57"
 */
export function formatCurrency(
  value: number,
  decimals: number = 2,
  showSymbol: boolean = true
): string {
  if (!isFinite(value)) return '-';

  const formatted = Math.abs(value).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  const withSymbol = showSymbol ? `$${formatted}` : formatted;

  // Handle negative values with parentheses (accounting format)
  return value < 0 ? `(${withSymbol})` : withSymbol;
}

/**
 * Format a number as a percentage
 * @param value - The numeric value to format (as decimal, e.g., 0.15 for 15%)
 * @param decimals - Number of decimal places (default: 1)
 * @param isAlreadyPercent - If true, value is already in percent form (e.g., 15 not 0.15)
 * @returns Formatted percentage string (e.g., "15.0%")
 *
 * @example
 * formatPercentage(0.156) // "15.6%"
 * formatPercentage(15.6, 1, true) // "15.6%"
 * formatPercentage(0.156, 2) // "15.60%"
 */
export function formatPercentage(
  value: number,
  decimals: number = 1,
  isAlreadyPercent: boolean = false
): string {
  if (!isFinite(value)) return '-';

  const percentValue = isAlreadyPercent ? value : value * 100;
  return `${percentValue.toFixed(decimals)}%`;
}

/**
 * Format a number as a multiple (e.g., P/E ratio, EV/EBITDA)
 * @param value - The numeric value to format
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted multiple string (e.g., "12.5x")
 *
 * @example
 * formatMultiple(12.456) // "12.5x"
 * formatMultiple(12.456, 2) // "12.46x"
 */
export function formatMultiple(value: number, decimals: number = 1): string {
  if (!isFinite(value)) return '-';
  return `${value.toFixed(decimals)}x`;
}

/**
 * Format a number with commas and optional decimal places
 * @param value - The numeric value to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted number string (e.g., "1,234.56")
 *
 * @example
 * formatNumber(1234.567) // "1,234.57"
 * formatNumber(1234.567, 1) // "1,234.6"
 * formatNumber(1234567.89, 0) // "1,234,568"
 */
export function formatNumber(value: number, decimals: number = 2): string {
  if (!isFinite(value)) return '-';

  return value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Parse a financial value string to a number
 * Handles:
 * - Comma separators: "1,234.56" → 1234.56
 * - Parentheses for negatives: "(123.45)" → -123.45
 * - Currency symbols: "$123.45" → 123.45
 * - Dashes for missing: "-" → null
 * - "NM" (Not Meaningful) → null
 *
 * @param value - The string value to parse
 * @returns Parsed number or null if invalid/missing
 *
 * @example
 * parseFinancialValue("1,234.56") // 1234.56
 * parseFinancialValue("(123.45)") // -123.45
 * parseFinancialValue("$1,234.56") // 1234.56
 * parseFinancialValue("-") // null
 * parseFinancialValue("NM") // null
 */
export function parseFinancialValue(value: string): number | null {
  if (!value || value === '-' || value.toUpperCase() === 'NM') {
    return null;
  }

  // Remove all non-numeric characters except decimal point, minus sign, and parentheses
  const cleaned = value.replace(/[^-\d.()]/g, '');

  // Check for parentheses (negative number in accounting format)
  const isNegative = cleaned.startsWith('(') && cleaned.endsWith(')');
  const numericString = isNegative ? cleaned.slice(1, -1) : cleaned;

  const parsed = parseFloat(numericString);

  if (!isFinite(parsed)) {
    return null;
  }

  return isNegative ? -parsed : parsed;
}

/**
 * Format a date to a readable string
 * @param date - Date object or date string
 * @param format - Format style ('short', 'medium', 'long')
 * @returns Formatted date string
 *
 * @example
 * formatDate(new Date('2024-03-15')) // "Mar 15, 2024"
 * formatDate('2024-03-15', 'short') // "3/15/2024"
 * formatDate('2024-03-15', 'long') // "March 15, 2024"
 */
export function formatDate(
  date: Date | string,
  format: 'short' | 'medium' | 'long' = 'medium'
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (!(dateObj instanceof Date) || !isFinite(dateObj.getTime())) {
    return '-';
  }

  const options: Intl.DateTimeFormatOptions =
    format === 'short'
      ? { month: 'numeric', day: 'numeric', year: 'numeric' }
      : format === 'long'
      ? { month: 'long', day: 'numeric', year: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' };

  return dateObj.toLocaleDateString('en-US', options);
}

/**
 * Format a number in millions notation
 * @param value - The numeric value in millions
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted string with "M" suffix (e.g., "123.4M")
 *
 * @example
 * formatMillions(123.456) // "123.5M"
 * formatMillions(1234.567, 0) // "1,235M"
 */
export function formatMillions(value: number, decimals: number = 1): string {
  if (!isFinite(value)) return '-';

  const formatted = value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return `${formatted}M`;
}

/**
 * Format a number in billions notation
 * @param value - The numeric value in billions
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted string with "B" suffix (e.g., "12.3B")
 *
 * @example
 * formatBillions(12.345) // "12.3B"
 * formatBillions(1.234, 2) // "1.23B"
 */
export function formatBillions(value: number, decimals: number = 1): string {
  if (!isFinite(value)) return '-';

  const formatted = value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return `${formatted}B`;
}

/**
 * Automatically format large numbers with appropriate suffix (K, M, B, T)
 * @param value - The numeric value
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted string with appropriate suffix
 *
 * @example
 * formatLargeNumber(1234) // "1.2K"
 * formatLargeNumber(1234567) // "1.2M"
 * formatLargeNumber(1234567890) // "1.2B"
 */
export function formatLargeNumber(value: number, decimals: number = 1): string {
  if (!isFinite(value)) return '-';

  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (absValue >= 1e12) {
    return `${sign}${(absValue / 1e12).toFixed(decimals)}T`;
  } else if (absValue >= 1e9) {
    return `${sign}${(absValue / 1e9).toFixed(decimals)}B`;
  } else if (absValue >= 1e6) {
    return `${sign}${(absValue / 1e6).toFixed(decimals)}M`;
  } else if (absValue >= 1e3) {
    return `${sign}${(absValue / 1e3).toFixed(decimals)}K`;
  } else {
    return `${sign}${absValue.toFixed(decimals)}`;
  }
}

/**
 * Determine the appropriate formatter based on context
 * Used by table updaters to automatically format values
 *
 * @param value - The value to format
 * @param columnName - The column name (to infer format type)
 * @param decimals - Number of decimal places
 * @returns Formatted string
 */
export function formatByContext(
  value: number | string,
  columnName: string,
  decimals: number = 2
): string {
  if (typeof value === 'string') {
    return value;
  }

  if (!isFinite(value)) {
    return '-';
  }

  const lowerName = columnName.toLowerCase();

  // Percentage columns
  if (
    lowerName.includes('margin') ||
    lowerName.includes('growth') ||
    lowerName.includes('yield') ||
    lowerName.includes('return')
  ) {
    return formatPercentage(value, 1, true);
  }

  // Multiple columns
  if (
    lowerName.includes('multiple') ||
    lowerName.includes('ratio') ||
    lowerName.includes('p/e') ||
    lowerName.includes('ev/') ||
    lowerName.includes('price/')
  ) {
    return formatMultiple(value, 1);
  }

  // Currency/number columns
  return formatNumber(value, decimals);
}

/**
 * Validate if a value is a valid number
 * @param value - Value to validate
 * @returns True if value can be parsed as a number
 */
export function isValidNumber(value: unknown): value is number {
  return typeof value === 'number' && isFinite(value);
}

/**
 * Safely convert a value to a number, returning a fallback if invalid
 * @param value - Value to convert
 * @param fallback - Fallback value if conversion fails (default: 0)
 * @returns Converted number or fallback
 */
export function toNumber(value: unknown, fallback: number = 0): number {
  if (typeof value === 'number' && isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = parseFinancialValue(value);
    return parsed !== null ? parsed : fallback;
  }

  return fallback;
}
